import React, { useEffect, useMemo, useState } from "react";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Video from "yet-another-react-lightbox/plugins/video";
import "./gallery-collage.css";
import HeroCollage from "./HeroCollage";
import { amenityIconFilterClass } from "../../../utils/amenityIcon";

const PLACEHOLDER =
  "https://static.vecteezy.com/system/resources/previews/005/337/799/original/icon-image-not-found-free-vector.jpg";

const getMediaSrc = (item) => item?.media?.file || PLACEHOLDER;

const isVideoMedia = (item) => {
  const type = item?.media?.type || item?.type;
  if (String(type).toUpperCase() === "VIDEO") return true;
  const src = getMediaSrc(item);
  return /\.(mp4|webm|mov|m4v|avi)(\?|$)/i.test(String(src));
};

const guessVideoMime = (src) => {
  const lower = String(src).toLowerCase();
  if (lower.includes(".webm")) return "video/webm";
  if (lower.includes(".mov")) return "video/quicktime";
  return "video/mp4";
};

const AutoPlayMedia = ({ item, className, alt }) => {
  const src = item ? getMediaSrc(item) : PLACEHOLDER;
  if (item && isVideoMedia(item)) {
    return (
      <video
        src={src}
        className={className}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        aria-label={alt}
      />
    );
  }
  return <img src={src} alt={alt} className={className} />;
};

const GalleryAmenity = ({ villaData }) => {
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const orderedMedia = useMemo(() => {
    const media = Array.isArray(villaData?.media) ? [...villaData.media] : [];
    const order = Array.isArray(villaData?.media_order)
      ? villaData.media_order
      : [];
    if (order.length === 0) return media;
    return media.sort((a, b) => {
      const idA = a.mediafiles_id ?? a?.media?.id;
      const idB = b.mediafiles_id ?? b?.media?.id;
      const indexA = order.indexOf(idA);
      const indexB = order.indexOf(idB);
      const safeA = indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA;
      const safeB = indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB;
      return safeA - safeB;
    });
  }, [villaData?.media, villaData?.media_order]);

  const slides = useMemo(
    () =>
      orderedMedia.map((item) => {
        const src = getMediaSrc(item);
        if (isVideoMedia(item)) {
          return {
            type: "video",
            width: 1920,
            height: 1080,
            sources: [{ src, type: guessVideoMime(src) }],
          };
        }
        return { src };
      }),
    [orderedMedia]
  );

  const collageItems = useMemo(
    () =>
      orderedMedia.map((item) => ({
        src: getMediaSrc(item),
        isVideo: isVideoMedia(item),
      })),
    [orderedMedia]
  );

  const freeAmenities = (villaData?.amenities || []).filter(
    (amenity) => !(villaData?.amenities_paid || []).includes(amenity.id)
  );
  const paidAmenities = (villaData?.amenities || []).filter((amenity) =>
    (villaData?.amenities_paid || []).includes(amenity.id)
  );

  const openLibrary = () => setLibraryOpen(true);

  const openLightboxAt = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  useEffect(() => {
    if (!libraryOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [libraryOpen]);

  return (
    <>
      <div className="lg:bg-[#EAEAEA] font-jt h-fit w-[99%] lg:w-[95vw] p-[2px] lg:p-4 rounded-3xl lg:rounded-3xl mt-2 mx-auto">
        {/* Desktop collage — StayVista-style */}
        <div className="hidden lg:block relative">
          <HeroCollage
            items={collageItems}
            onOpenAt={openLightboxAt}
            onShowAll={openLibrary}
            onViewVideo={() => {
              const videoIndex = collageItems.findIndex((item) => item.isVideo);
              if (videoIndex < 0) return;
              openLightboxAt(videoIndex);
            }}
          />
        </div>

        <div className="lg:hidden block p-2 ml-2 text-[30px] font-bold ">
          Amenities at Property
        </div>

        <div className="lg:hidden block px-2 ml-2 text-[25px] font-bold ">
          Free
        </div>
        <div className="hidden lg:block p-2 ml-2 text-[30px] font-bold ">
          Free Amenities at Property
        </div>
        <div className=" p-2 justify-center lg:justify-start lg:ml-2 w-full flex flex-wrap flex-row   lg:h-auto lg:flex-row">
          {freeAmenities.map((a) => (
            <div
              key={a.id}
              className="flex flex-col w-[80px] h-[80px] items-cemter lg:h-auto rounded-md lg:w-auto lg:flex-row mt-2   lg:min-w-[200px] bg-orange-200 lg:bg-white lg:rounded-full p-1 justify-center lg:p-2 mx-1 lg:mx-2 lg:px-4 items-center"
            >
              <div className="mr-2 ">
                <img
                  className={`lg:w-8 lg:h-8 w-8 h-8 object-contain ${amenityIconFilterClass(a.name)}`}
                  src={a.icon.file}
                  alt={a.icon.file}
                />
              </div>
              {a.name.length > 15 ? (
                <div className="text-[11.5px] -tracking-wider lg:tracking-normal text-center lg:text-lg">
                  {a.name}
                </div>
              ) : (
                <div className="text-sm text-center lg:text-lg">{a.name}</div>
              )}
            </div>
          ))}
        </div>
        {villaData?.amenities_paid?.length > 0 ? (
          <>
            <div className="lg:hidden block px-2 ml-2 text-[25px] font-bold ">
              Paid
            </div>
            <div className="hidden lg:block p-2 ml-2 text-[30px] font-bold ">
              Paid Amenities at Property
            </div>
            <div className=" p-2 justify-center lg:justify-start lg:ml-2 w-full flex flex-wrap flex-row   lg:h-auto lg:flex-row">
              {paidAmenities.map((a) => (
                <div
                  key={a.id}
                  className="flex flex-col w-[80px] h-[80px] items-cemter lg:h-auto rounded-md lg:w-auto lg:flex-row mt-2   lg:min-w-[200px] bg-purple-200 lg:bg-white lg:rounded-full p-1 justify-center lg:p-2 mx-1 lg:mx-2 lg:px-4 items-center"
                >
                  <div className="mr-2 ">
                    <img
                      className={`lg:w-8 lg:h-8 w-8 h-8 object-contain ${amenityIconFilterClass(a.name)}`}
                      src={a.icon.file}
                      alt={a.icon.file}
                    />
                  </div>
                  {a.name.length > 15 ? (
                    <div className="text-[11.5px] -tracking-wider lg:tracking-normal text-center lg:text-lg">
                      {a.name}
                    </div>
                  ) : (
                    <div className="text-sm text-center lg:text-lg">{a.name}</div>
                  )}
                </div>
              ))}
            </div>
          </>
        ) : null}

        {/* Modern photo library */}
        {libraryOpen ? (
          <div className="fixed inset-0 z-[100000] flex flex-col bg-[#F7F7F7]">
            <header className="sticky top-0 z-20 flex items-center justify-between border-b border-black/5 bg-white/90 px-4 py-3 backdrop-blur-md lg:px-8">
              <div>
                <h2 className="text-lg lg:text-xl font-bold text-[#222]">
                  Photo gallery
                </h2>
                <p className="text-sm text-[#717171]">
                  {slides.length} item{slides.length === 1 ? "" : "s"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setLibraryOpen(false)}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-black/10 bg-white text-[#222] shadow-sm hover:bg-[#F0F0F0] transition-colors"
                aria-label="Close gallery"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d="M1 1l12 12M13 1L1 13"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </header>

            <div className="flex-1 overflow-y-auto custom-scrollbar px-3 py-4 lg:px-10 lg:py-8">
              <div className="mx-auto max-w-6xl gallery-library-grid">
                {orderedMedia.length === 0 ? (
                  <div className="rounded-2xl bg-white p-10 text-center text-[#717171]">
                    No photos available
                  </div>
                ) : (
                  orderedMedia.map((item, index) => {
                    const video = isVideoMedia(item);
                    return (
                      <button
                        key={`lib-${index}-${getMediaSrc(item)}`}
                        type="button"
                        onClick={() => openLightboxAt(index)}
                        className="gallery-library-tile group focus:outline-none focus-visible:ring-2 focus-visible:ring-black/30"
                      >
                        <AutoPlayMedia
                          item={item}
                          alt={`Property media ${index + 1}`}
                          className="transition-transform duration-500 group-hover:scale-[1.03]"
                        />
                        {video ? (
                          <span className="absolute top-3 left-3 rounded-full bg-black/60 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-white">
                            Video
                          </span>
                        ) : null}
                        <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                        <span className="pointer-events-none absolute bottom-3 left-3 rounded-full bg-black/55 px-2.5 py-1 text-xs font-medium text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                          {index + 1} / {orderedMedia.length}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        ) : null}

        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          index={lightboxIndex}
          slides={slides}
          plugins={[Thumbnails, Zoom, Video]}
          on={{
            view: ({ index }) => setLightboxIndex(index),
          }}
          styles={{
            container: { backgroundColor: "rgba(0,0,0,0.92)" },
          }}
        />
      </div>
    </>
  );
};

export default GalleryAmenity;
